import { Component, Input, OnDestroy, OnInit, SimpleChange, OnChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AhsTextComponent, AhsTextareaComponent } from '@nx-ahs/base-ahs-controls/reactive';
import { Subscription } from 'rxjs';

import { ClientTranslationKeyModel } from '../models/language-locale.model';

import { TranslationManagerService } from '../services/translation-manager.service';
import { UserEditStorageService } from '../services/user-edit-storage/user-edit-storage.service';

@Component({
	selector: 'client-translation-admin-pane',
	templateUrl: './client-translation-admin-pane.component.html',
	styleUrls: ['./client-translation-admin-pane.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatFormFieldModule,
		MatSelectModule,
		FlexLayoutModule,
		FontAwesomeModule,
		AhsTextComponent,
		AhsTextareaComponent
	]
})
export class ClientTranslationAdminPaneComponent implements OnInit, OnDestroy, OnChanges {
	@Input()
	public clientTranslationKey: ClientTranslationKeyModel = null;

	public translationUpdateForm: FormGroup = null;

	private formChanges: Subscription = null;
	private readonly DISABLE_LANG_CODE_EDITS: boolean = true;

	constructor(
		private formBuilder: FormBuilder,
		private translationManagerService: TranslationManagerService,
		private userEditStorageService: UserEditStorageService
	) {}

	ngOnInit(): void {
		this.configureTranslationUpdateForm();
	}

	/**
	 * For any clientTranslationKey changes that are not the first change
	 * Rebuild the translationUpdateForm with new values
	 */
	ngOnChanges(changes: Record<string, SimpleChange>) {
		if (!changes['clientTranslationKey'].firstChange) {
			this.resetTranlationFormValues();
		}
	}

	ngOnDestroy() {
		this.formChanges.unsubscribe();
	}

	public get updateFormLanguagesFormArray() {
		return this.translationUpdateForm.controls['translationKeyLanguages'] as FormArray;
	}

	public get translationKeyLanguagesControlArray() {
		return this.updateFormLanguagesFormArray.controls;
	}

	public get outstandingLanguageEdits() {
		return this.userEditStorageService.hasOutstandingChanges;
	}

	//TODO - This needs better error handling and documentation. REFACTOR TO USER EDIT STORAGE
	public async postChanges() {
		if (this.allowSave()) {
			if (this.userEditStorageService.hasOutstandingChanges) {
				try {
					const result = await this.translationManagerService.mergeClientTranslationKeysToDb(
						this.userEditStorageService.modifiedClientTranslationKey
					);

					if (result) {
						this.clearEditStorage();
						alert('Save was succesfull!');
					}
				} catch (e) {
					throw new Error('Something went wrong when posting changes' + e);
				}
			}
		}
	}

	/**
	 * For all DB Keys that are missing one or more contract language codes, Creates any missing language code translations and retranslates all existing records.
	 * Records must contain an EN version to be processed.
	 */
	public async createAndReTranslateProvidedEnglishKeys(keyToTranslate: ClientTranslationKeyModel) {
		await this.translationManagerService
			.createAndReTranslateProvidedEnglishKeys(keyToTranslate)
			.then((translatedKey) => {
				alert(JSON.stringify(translatedKey, null, 2).toString());
			})
			.catch((error) => {
				console.log(JSON.stringify(error));
				alert(error.status + ': ' + error.error);
			});
	}

	/**
	 * Rebuilds the translation update form with the values of clientTranslationKey
	 * This method assumes a form has already been built to unsubscribe from.
	 */
	public resetTranlationFormValues(): void {
		this.resetTranslationUpdateForm();
		this.formChanges.unsubscribe();
		this.configureTranslationUpdateForm();
	}

	/**
	 * Constructs the language update form. Builds a form array with a form group for each language
	 * provided in the translation.
	 */
	private configureTranslationUpdateForm(): void {
		this.translationUpdateForm = this.formBuilder.group({
			translationKeyLanguages: new FormArray([])
		});

		this.clientTranslationKey.languages.forEach((language) => {
			const languageFormGroup = this.formBuilder.group({
				languageCode: [
					{ value: language.languageCode, disabled: this.DISABLE_LANG_CODE_EDITS },
					Validators.required
				],
				translatedText: [language.translatedText, Validators.required]
			});
			this.updateFormLanguagesFormArray.push(languageFormGroup);
		});
		this.trackLanguageEdits();
	}

	/**
	 * Builds a new ClientTranslationKeyModel each time the form is changed. UserEditStorageService is update with new value.
	 * @returns void
	 */
	private trackLanguageEdits(): void {
		this.formChanges = this.updateFormLanguagesFormArray.valueChanges.subscribe((languageEdits) => {
			this.userEditStorageService.modifiedClientTranslationKey = {
				key: this.clientTranslationKey.key,
				languages: this.DISABLE_LANG_CODE_EDITS
					? [...this.updateFormLanguagesFormArray.getRawValue()]
					: [...languageEdits],
				portalPageName: this.clientTranslationKey.portalPageName,
				clientTypeId: this.clientTranslationKey.clientTypeId,
				clientTypeName: this.clientTranslationKey.clientTypeName
			};
		});
	}

	/**
	 * @remarks Removes all form controls from the FormGroup
	 * Clears user edit storage
	 * @sets translationUpdateForm = null
	 */
	private resetTranslationUpdateForm(): void {
		this.translationUpdateForm.removeControl('translationKeyLanguages');
		this.translationUpdateForm = null;
		this.clearEditStorage();
	}

	/**
	 * Clears user edit storage
	 */
	private clearEditStorage(): void {
		this.userEditStorageService.modifiedClientTranslationKey = null;
	}

	/**
	 * TODO - Refactor to user edit storage service
	 * @remarks Determines the user changes are allowed to be saved
	 * @returns boolean - True if there are outstanding changes and form is valid. False otherwise.
	 */
	public allowSave(): boolean {
		if (this.outstandingLanguageEdits && this.translationUpdateForm.valid) {
			return true;
		}
		return false;
	}

	/**
	 * Updates the IsActive flag for a portal page translation key mapping
	 * @param pageNameClientLanguageId The ID of the PageNameClientLanguage record
	 * @param isActive The new IsActive value
	 */
	public async updatePortalPageIsActive(pageNameClientLanguageId: number, isActive: boolean) {
		try {
			const result = await this.translationManagerService.updatePortalPageIsActive(
				pageNameClientLanguageId,
				isActive
			);
			if (result) {
				// Update the local model
				const portalPage = this.clientTranslationKey.portalPages?.find(
					(pp) => pp.pageNameClientLanguageId === pageNameClientLanguageId
				);
				if (portalPage) {
					portalPage.isActive = isActive;
				}
				alert('Portal page status updated successfully!');
			}
		} catch (e) {
			console.error('Error updating portal page status', e);
			alert('Failed to update portal page status');
		}
	}
}
